import type { ReactNode } from 'react';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { visibleActions, type DesktopAction } from './types';

/**
 * The toolbar above a grid. Its buttons act on whatever is selected.
 *
 * An action the caller has no permission for is absent; one the row's own
 * state forbids is present but disabled, with the reason on hover. Hiding the
 * first avoids offering something that can never work; showing the second is
 * how someone learns that a posted document has to be reversed, not edited.
 */
export function ActionBar({ actions, children }: { actions: DesktopAction[]; children?: ReactNode }) {
  const shown = visibleActions(actions);
  if (shown.length === 0 && !children) return null;

  return (
    <div className="actionbar">
      {shown.map((a) => (
        <button
          key={a.label}
          type="button"
          className={`btn btn--sm${a.primary ? ' btn--primary' : ''}${a.danger ? ' btn--danger' : ''}${a.icon ? ' btn--withicon' : ''}`}
          disabled={a.enabled === false}
          title={a.enabled === false ? a.reason : undefined}
          onClick={a.onSelect}
        >
          {a.icon && <AppIcon kind={a.icon} size={15} />}
          {a.label}
        </button>
      ))}
      {children && <div className="actionbar__extra">{children}</div>}
    </div>
  );
}

export interface DetailTab {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  /** Drawn before the tab's label. */
  icon?: IconKind;
}

/** The pane under the grid, showing whatever row is selected. */
export function DetailPane({
  tabs,
  activeTab,
  onTabChange,
  empty = 'Choose a row to see its details.',
  title,
  actions,
}: {
  tabs: DetailTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  empty?: string;
  title?: ReactNode;
  actions?: DesktopAction[];
}) {
  const shown = tabs.filter((t) => !t.hidden);
  if (shown.length === 0) {
    return (
      <div className="detailpane detailpane--empty">
        <p className="muted">{empty}</p>
      </div>
    );
  }

  const active = shown.find((t) => t.id === activeTab) ?? shown[0]!;

  return (
    <section className="detailpane" aria-label="Details">
      <div className="detailpane__head">
        <div className="detailpane__tabs" role="tablist" aria-label="Detail sections">
          {shown.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`detailtab-${t.id}`}
              aria-selected={t.id === active.id}
              aria-controls={`detailpanel-${t.id}`}
              tabIndex={t.id === active.id ? 0 : -1}
              disabled={t.disabled}
              className={`detailpane__tab${t.id === active.id ? ' detailpane__tab--active' : ''}${t.icon ? ' detailpane__tab--withicon' : ''}`}
              onClick={() => onTabChange(t.id)}
              onKeyDown={(e) => {
                const index = shown.findIndex((x) => x.id === active.id);
                if (e.key === 'ArrowRight') onTabChange(shown[(index + 1) % shown.length]!.id);
                else if (e.key === 'ArrowLeft') onTabChange(shown[(index - 1 + shown.length) % shown.length]!.id);
                else return;
                e.preventDefault();
              }}
            >
              {t.icon && <AppIcon kind={t.icon} size={14} />}
              {t.label}
            </button>
          ))}
        </div>
        {title && <div className="detailpane__title">{title}</div>}
      </div>

      {actions && <ActionBar actions={actions} />}

      <div
        className="detailpane__body"
        role="tabpanel"
        id={`detailpanel-${active.id}`}
        aria-labelledby={`detailtab-${active.id}`}
      >
        {active.content}
      </div>
    </section>
  );
}

/** The line along the bottom: what is shown, what is selected, what matters. */
export function StatusBar({ children }: { children: ReactNode }) {
  return (
    <div className="statusbar" role="status">
      {children}
    </div>
  );
}
